/**
 * Single source of truth for the customer portal mock surface.
 * Everything is generated deterministically from a fixed seed so the
 * UI is stable between reloads (and between tabs).
 */
import {
  generateApiKeys,
  generateApprovalRequests,
  generateAuditEvents,
  generateErpConnectors,
  generateInboundInvoices,
  generateInvoiceTemplates,
  generateNotifications,
  generateRecoveryAuditFindings,
  generateSampleInvoices,
  generateSampleUsers,
  generateSpendOverview,
  generateTddSubmissions,
  generateUnspscEntries,
  generateVatOverview,
  generateArchiveRecords,
  summariseRecoveryAudit,
  type ApiKey,
  type ApprovalRequest,
  type ArchiveRecord,
  type AuditEvent,
  type ErpConnectorStatus,
  type InboundInvoice,
  type InvoiceTemplate,
  type RecoveryAuditFinding,
  type RecoveryAuditTotals,
  type SampleInvoice,
  type SampleNotification,
  type SampleUser,
  type SpendOverview,
  type TddSubmission,
  type UnspscEntry,
  type VatOverview,
} from '@solara/sample-data';

export const MOCK_INVOICES: SampleInvoice[] = generateSampleInvoices(28, 0xc0ffee).map((inv, idx) => ({
  ...inv,
  direction: 'OUTBOUND',
  mlsStatus: idx === 0 ? 'PENDING' : idx === 1 ? 'AB' : idx === 2 ? 'RE' : inv.mlsStatus,
}));

export const MOCK_TDD: TddSubmission[] = generateTddSubmissions(MOCK_INVOICES);
export const MOCK_ARCHIVE: ArchiveRecord[] = generateArchiveRecords(MOCK_INVOICES);

export const MOCK_USERS: SampleUser[] = generateSampleUsers(22);
export const MOCK_NOTIFICATIONS: SampleNotification[] = generateNotifications(22);
export const MOCK_TEMPLATES: InvoiceTemplate[] = generateInvoiceTemplates();
export const MOCK_INBOUND: InboundInvoice[] = generateInboundInvoices(22);
export const MOCK_APPROVALS: ApprovalRequest[] = generateApprovalRequests(22);
export const MOCK_AUDIT: AuditEvent[] = generateAuditEvents(30);
export const MOCK_ERP: ErpConnectorStatus[] = generateErpConnectors();
export const MOCK_API_KEYS: ApiKey[] = generateApiKeys();

export const MOCK_SPEND: SpendOverview = generateSpendOverview(MOCK_INVOICES);
export const MOCK_VAT: VatOverview = generateVatOverview(MOCK_INVOICES);
export const MOCK_RECOVERY_AUDIT: RecoveryAuditFinding[] = generateRecoveryAuditFindings(28);
export const MOCK_RECOVERY_AUDIT_TOTALS: RecoveryAuditTotals = summariseRecoveryAudit(MOCK_RECOVERY_AUDIT);
export const MOCK_UNSPSC: UnspscEntry[] = generateUnspscEntries();

export function findInvoice(id: string): SampleInvoice | undefined {
  return MOCK_INVOICES.find((i) => i.invoiceId === id);
}
